import { useState, useEffect, useRef } from 'react'
import { Button, Popup, Tag, Toast, Loading, Empty, Dialog } from 'react-vant'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import teacherAffairsApi from '../../../api/teacherAffairs'

const LEAVE_TYPE = { sick: { label: '病假', color: '#e11d48', icon: '🏥' }, personal: { label: '事假', color: '#fa8c16', icon: '📋' } }
const PERIOD_LABEL_MAP = { morning: '上午', afternoon: '下午' }
const STATUS_MAP = {
  pending: { label: '待审批', color: '#fa8c16', bg: '#fffbeb' },
  approved: { label: '已通过', color: '#00b578', bg: '#ecfdf5' },
  rejected: { label: '已驳回', color: '#e11d48', bg: '#fff1f2' },
  auto_approved: { label: '自动通过', color: '#00b578', bg: '#ecfdf5' },
}

export default function LeavePage() {
  const navigate = useNavigate()
  const { userInfo } = useSelector(state => state.user)
  const [records, setRecords] = useState([])
  const [balances, setBalances] = useState([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)

  // 表单
  const [formType, setFormType] = useState('sick')
  const [formStartDate, setFormStartDate] = useState('')
  const [formEndDate, setFormEndDate] = useState('')
  const [formPeriod, setFormPeriod] = useState('full')
  // 单选「请假时段」→ 映射成后端的 start_period / end_period
  //   上午 → morning/morning    下午 → afternoon/afternoon    全天 → morning/afternoon
  const PERIOD_OPTIONS = {
    morning: { start_period: 'morning', end_period: 'morning', label: '上午' },
    afternoon: { start_period: 'afternoon', end_period: 'afternoon', label: '下午' },
    full: { start_period: 'morning', end_period: 'afternoon', label: '全天' },
  }
  const [formDays, setFormDays] = useState('')
  const [formReason, setFormReason] = useState('')
  const [formFreeLeave, setFormFreeLeave] = useState(false)
  // 倒课：是否影响课时 + 受影响课程确认
  const [formHasImpact, setFormHasImpact] = useState(false)
  // 凭证上传（病假必传挂号凭证，后端会强校验）
  const [attachments, setAttachments] = useState([])
  const [uploading, setUploading] = useState(false)
  // 「病假必须上传凭证」由「事务设置」决定，默认按后端默认值为 true
  const [sickRequireAtt, setSickRequireAtt] = useState(true)
  // 天数自动计算（按校历教学日）
  const [daysHint, setDaysHint] = useState('')
  const [calcBusy, setCalcBusy] = useState(false)
  // 手动改过天数后不再被自动计算覆盖（用 ref，避免进 useEffect 依赖）
  const daysTouchedRef = useRef(false)
  const [impactRows, setImpactRows] = useState([])
  const [impactMsg, setImpactMsg] = useState('')
  const [detecting, setDetecting] = useState(false)
  const [showImpacts, setShowImpacts] = useState(false)

  useEffect(() => { fetchRecords(); fetchBalances() }, [page])

  // 读事务配置：病假是否必须上传凭证（与后端规则保持一致，
  // 否则管理员把该开关关掉也没用 —— 前端仍会拦着不让提交）
  useEffect(() => {
    teacherAffairsApi.getConfig().then(res => {
      const cfg = (Array.isArray(res) ? res : (res?.data || [])).find(c => c.config_key === 'leave_sick_require_attachment');
      if (cfg) setSickRequireAtt(String(cfg.config_value).toLowerCase() === 'true');
    }).catch(() => {});
  }, [])

  const fetchRecords = async () => {
    setLoading(true)
    try {
      const res = await teacherAffairsApi.getMyLeaveList({ page, page_size: 20 })
      setRecords(res?.items || [])
      setTotal(res?.total || 0)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  const fetchBalances = async () => {
    if (!userInfo?.id) return
    try {
      const res = await teacherAffairsApi.getLeaveBalance(userInfo.id)
      setBalances(Array.isArray(res) ? res : (res?.data || []))
    } catch (e) { /* ignore */ }
  }

  const resetForm = () => {
    setFormType('sick'); setFormStartDate(''); setFormEndDate('')
    setFormPeriod('full')
    setFormDays(''); setFormReason(''); setFormFreeLeave(false)
  }

  // 上传凭证
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''   // 允许重复选同一文件
    if (!files.length) return
    setUploading(true)
    let okCount = 0
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) {
        Toast.fail(`${f.name} 超过 10MB`); continue
      }
      const fd = new FormData()
      fd.append('file', f)
      try {
        const res = await teacherAffairsApi.uploadAttachment(fd)
        setAttachments(prev => [...prev, res?.data || res])
        okCount++
      } catch (err) {
        Toast.fail(err.response?.data?.detail || '上传失败')
      }
    }
    setUploading(false)
    if (okCount) Toast.success(`已上传 ${okCount} 个凭证`)
  }

  const removeAttachment = (url) => {
    setAttachments(prev => prev.filter(a => a.url !== url))
  }

  // 日期 / 时段变化 → 自动算天数（教师仍可手动改）
  //
  // 用 useEffect 而不是 onChange 里调函数：onChange 里 setState 尚未生效，
  // 回调里读到的仍是**旧 state** → 永远算不出天数（曾因此功能完全失效）。
  useEffect(() => {
    if (!formStartDate || !formEndDate) { setDaysHint(''); return }
    if (formEndDate < formStartDate) {
      setDaysHint('结束日期不能早于开始日期'); return
    }
    let cancelled = false
    setCalcBusy(true)
    teacherAffairsApi.calculateLeaveDays({
      start_date: formStartDate, end_date: formEndDate,
      start_period: PERIOD_OPTIONS[formPeriod].start_period,
      end_period: PERIOD_OPTIONS[formPeriod].end_period,
    }).then(res => {
      if (cancelled) return
      const d = res?.data || res || {}
      if (!daysTouchedRef.current) setFormDays(String(d.duration_days ?? ''))
      setDaysHint(d.message || '')
    }).catch(e => {
      if (!cancelled) setDaysHint(e.response?.data?.detail || '天数计算失败')
    }).finally(() => { if (!cancelled) setCalcBusy(false) })
    return () => { cancelled = true }
  }, [formStartDate, formEndDate, formPeriod])

  const handleSubmit = async () => {
    if (!formStartDate || !formEndDate || !formDays || !formReason) {
      Toast.info('请填写完整信息'); return
    }
    // 病假必须上传挂号凭证（与后端规则一致，避免提交后被 400 拒绝）
    if (sickRequireAtt && formType === 'sick' && attachments.length === 0) {
      Toast.fail('病假需上传挂号凭证'); return
    }
    try {
      const res = await teacherAffairsApi.createLeave({
        leave_type: formType, start_date: formStartDate, end_date: formEndDate,
        start_period: PERIOD_OPTIONS[formPeriod].start_period,
        end_period: PERIOD_OPTIONS[formPeriod].end_period,
        duration_days: parseFloat(formDays), reason: formReason,
        is_free_leave: formFreeLeave,
        attachments: attachments.length ? attachments : undefined,
      })
      const leaveId = res?.data?.id || res?.id

      // 勾选了「影响课时」→ 提交后自动识别并让教师确认
      if (formHasImpact && leaveId) {
        try {
          const dres = await teacherAffairsApi.detectLeaveImpacts(leaveId, {})
          const rows = dres?.data?.impacts || dres?.impacts || []
          setImpactRows(rows)
          setImpactMsg(dres?.data?.message || dres?.message || '')
          if (rows.length > 0) {
            setShowImpacts(true)
          } else {
            Toast.success('提交成功，未涉及课时')
          }
        } catch (err) {
          Toast.success('提交成功')
          Toast.info('未能自动识别受影响课时，请稍后在记录中查看')
        }
      } else {
        Toast.success('提交成功')
      }
      setShowForm(false); resetForm(); setAttachments([]); fetchRecords(); fetchBalances()
    } catch (e) { Toast.fail(e.response?.data?.detail || '提交失败') }
  }

  // 教师标记某节课「免处理 / 无需倒课」
  const markImpact = async (row, resolutionType) => {
    try {
      await teacherAffairsApi.updateLeaveImpact(row.id, {
        resolution_type: resolutionType,
        status: resolutionType === 'no_impact' ? 'skipped' : 'confirmed',
      })
      setImpactRows(prev => prev.map(r => r.id === row.id
        ? { ...r, resolution_type: resolutionType, status: resolutionType === 'no_impact' ? 'skipped' : 'confirmed' }
        : r))
      Toast.success('已标记')
    } catch (e) { Toast.fail('操作失败') }
  }

  // pending=true → 撤销（待审批）；pending=false → 删除（已通过/已驳回）
  const handleCancel = async (id, pending = true) => {
    try {
      const isPending = pending === true
      await Dialog.confirm({
        title: isPending ? '确认撤销' : '确认删除',
        message: isPending
          ? '确认撤销该请假申请？'
          : '确认删除该请假记录？删除后不可恢复；若已安排代课，需先请教务取消代课。',
      })
      await teacherAffairsApi.cancelLeave(id)
      Toast.success(isPending ? '已撤销' : '已删除'); fetchRecords(); fetchBalances()
    } catch (e) { if (e !== 'cancel') Toast.fail('操作失败') }
  }

  const totalBalance = balances.reduce((sum, b) => sum + (b.remaining_days || 0), 0)
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }

  return (
    <div style={{ background: '#f5f8f6', minHeight: '100vh', paddingBottom: 80 }}>
      <div style={{
        background: '#056b4f', padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div onClick={() => navigate(-1)} style={{ cursor: 'pointer', padding: 4 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>我的请假</span>
        <div style={{ width: 24 }} />
      </div>

      {/* 概览 Banner（三页统一结构与高度） */}
      <div style={{ background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)', padding: '0 16px 28px', borderRadius: '0 0 24px 24px', color: '#fff' }}>
        <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.2)', borderRadius: 14, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>免假余额</div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{Math.round(totalBalance * 10) / 10}<span style={{ fontSize: 13, fontWeight: 400 }}> 天</span></div>
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.2)', borderRadius: 14, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>请假记录</div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{total}<span style={{ fontSize: 13, fontWeight: 400 }}> 条</span></div>
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.2)', borderRadius: 14, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>待审批</div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{records.filter(r => r.approval_status === 'pending').length}<span style={{ fontSize: 13, fontWeight: 400 }}> 条</span></div>
        </div>
        </div>
      </div>

      {/* 各额度明细（放在 banner 下方，保持 banner 高度固定） */}
      {balances.length > 1 && (
        <div style={{ padding: '12px 16px 0', display: 'flex', gap: 8, overflowX: 'auto' }}>
          {balances.map(b => (
            <div key={b.id} style={{
              background: '#ecfdf5', borderRadius: 10, padding: '8px 12px',
              whiteSpace: 'nowrap', minWidth: 80,
            }}>
              <div style={{ fontSize: 11, color: '#64748b' }}>{b.config_name || b.period_key}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#056b4f', marginTop: 2 }}>{b.remaining_days}天</div>
            </div>
          ))}
        </div>
      )}

      {/* 记录列表 */}
      <div style={{ padding: '8px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>请假记录</span>
          <Tag type="primary" plain size="medium" style={{ color: '#056b4f', borderColor: '#056b4f' }}>{total} 条</Tag>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: 40 }}><Loading size={24} /></div>
        : records.length === 0 ? <Empty description="暂无请假记录" />
        : records.map(item => {
          const lt = LEAVE_TYPE[item.leave_type] || LEAVE_TYPE.sick
          const st = STATUS_MAP[item.approval_status] || STATUS_MAP.pending
          return (
            <div key={item.id} style={{
              background: '#fff', borderRadius: 12, padding: 14, marginBottom: 10,
              boxShadow: '0 1px 6px rgba(0,0,0,0.04)', borderLeft: `3px solid ${lt.color}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 16 }}>{lt.icon}</span>
                  <Tag plain style={{ color: lt.color, borderColor: lt.color }}>{lt.label}</Tag>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{item.duration_days}天</span>
                </div>
                <Tag plain style={{ color: st.color, borderColor: st.color, background: st.bg, fontSize: 11 }}>{st.label}</Tag>
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                {item.start_date} {PERIOD_LABEL_MAP[item.start_period]} ~ {item.end_date} {PERIOD_LABEL_MAP[item.end_period]}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{item.reason}</div>
              {item.is_free_leave && <Tag color="#00b578" style={{ marginTop: 4, fontSize: 11 }}>免假</Tag>}
              {item.rejection_reason && <div style={{ fontSize: 12, color: '#e11d48', marginTop: 4 }}>驳回：{item.rejection_reason}</div>}
              {item.approval_status === 'pending' ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <Button size="mini" type="danger" plain onClick={() => handleCancel(item.id, true)}>撤销</Button>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <Button size="mini" plain onClick={() => handleCancel(item.id, false)}>删除</Button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* 浮动按钮 */}
      <div style={{ position: 'fixed', bottom: 24, right: 20, zIndex: 100 }}>
        <Button round type="primary"
          style={{ background: '#056b4f', border: 'none', boxShadow: '0 4px 16px rgba(5,107,79,0.35)', width: 52, height: 52, fontSize: 24, color: '#fff' }}
          onClick={() => { resetForm(); setShowForm(true) }}>+</Button>
      </div>

      {/* 请假申请表单 */}
      <Popup visible={showForm} position="bottom" round closeable onClose={() => setShowForm(false)} style={{ maxHeight: '90vh' }}>
        <div style={{ padding: '20px 16px 32px', overflowY: 'auto', maxHeight: 'calc(90vh - 60px)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>请假申请</h3>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 14, color: '#475569', marginBottom: 6, display: 'block' }}>请假类型 *</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {Object.entries(LEAVE_TYPE).map(([k, v]) => (
                <div key={k} onClick={() => setFormType(k)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, textAlign: 'center', cursor: 'pointer',
                    border: formType === k ? `2px solid ${v.color}` : '2px solid #e2e8f0',
                    background: formType === k ? `${v.color}08` : '#fff',
                  }}>
                  <span style={{ fontSize: 20 }}>{v.icon}</span>
                  <div style={{ fontSize: 13, marginTop: 4, color: formType === k ? v.color : '#64748b' }}>{v.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 14, color: '#475569', marginBottom: 6, display: 'block' }}>开始日期 *</label>
              <input type="date" value={formStartDate} onChange={e => { daysTouchedRef.current = false; setFormStartDate(e.target.value) }} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 14, color: '#475569', marginBottom: 6, display: 'block' }}>结束日期 *</label>
              <input type="date" value={formEndDate} onChange={e => { daysTouchedRef.current = false; setFormEndDate(e.target.value) }} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 14, color: '#475569', marginBottom: 6, display: 'block' }}>请假时段</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {Object.entries(PERIOD_OPTIONS).map(([k, v]) => (
                  <button key={k} type="button"
                    onClick={() => { daysTouchedRef.current = false; setFormPeriod(k) }}
                    style={{
                      flex: 1, height: 44, borderRadius: 10, fontSize: 14,
                      border: `1px solid ${formPeriod === k ? '#056b4f' : '#e2e8f0'}`,
                      background: formPeriod === k ? '#056b4f' : '#fff',
                      color: formPeriod === k ? '#fff' : '#475569',
                    }}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 14, color: '#475569', marginBottom: 6, display: 'block' }}>
                天数 *{calcBusy ? <span style={{ color: '#94a3b8', fontSize: 12 }}> 计算中…</span> : null}
              </label>
              <input type="number" step="0.5" min="0.5" value={formDays}
                onChange={e => { daysTouchedRef.current = true; setFormDays(e.target.value) }}
                style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            {daysHint && (
              <div style={{
                fontSize: 12, color: daysHint.includes('不能早于') ? '#e11d48' : '#0891b2',
                marginBottom: 10, lineHeight: 1.5,
              }}>
                {daysHint}
                {!daysTouchedRef.current && '，如有特殊情况请联系教务调整'}
              </div>
            )}
            <label style={{ fontSize: 14, color: '#475569', marginBottom: 6, display: 'block' }}>原因 *</label>
            <textarea value={formReason} onChange={e => setFormReason(e.target.value)} placeholder="至少2字" rows={3} style={{ ...inputStyle, resize: 'none' }} />
          </div>

          {/* 上传凭证：病假必传挂号凭证（后端会强校验，缺了会 400） */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 14, color: '#475569', marginBottom: 4, display: 'block' }}>
              上传凭证
              {formType === 'sick' && sickRequireAtt
                ? <span style={{ color: '#e11d48', marginLeft: 4 }}>* 病假必传</span>
                : <span style={{ color: '#94a3b8', marginLeft: 4, fontSize: 12 }}>（可选）</span>}
            </label>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8, lineHeight: 1.5 }}>
              支持 jpg / png / pdf，单个不超过 10MB
            </div>

            {/* 已上传的凭证 */}
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {attachments.map(a => {
                  const isImg = /\.(jpg|jpeg|png|webp|heic)$/i.test(a.filename || a.url)
                  return (
                    <div key={a.url} style={{
                      width: 76, height: 76, borderRadius: 10, overflow: 'hidden',
                      border: '1px solid #e2e8f0', position: 'relative', background: '#f8fafc',
                    }}>
                      {isImg ? (
                        <img src={a.url} alt={a.filename}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{
                          width: '100%', height: '100%', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 11, color: '#64748b', padding: 4, textAlign: 'center',
                        }}>
                          {a.filename || '文件'}
                        </div>
                      )}
                      <div onClick={() => removeAttachment(a.url)}
                        style={{
                          position: 'absolute', top: 0, right: 0, width: 20, height: 20,
                          background: 'rgba(15,23,42,0.55)', color: '#fff', borderRadius: '0 10px 0 10px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, cursor: 'pointer', lineHeight: 1,
                        }}>×</div>
                    </div>
                  )
                })}
              </div>
            )}

            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 44, border: `1px dashed ${formType === 'sick' && sickRequireAtt && attachments.length === 0 ? '#fda4af' : '#cbd5e1'}`,
              borderRadius: 10, background: '#fff', cursor: uploading ? 'wait' : 'pointer',
              color: uploading ? '#94a3b8' : (formType === 'sick' && sickRequireAtt ? '#e11d48' : '#056b4f'),
              fontSize: 14,
            }}>
              <input type="file" accept="image/*,.pdf,.heic,.webp" multiple
                onChange={handleUpload} disabled={uploading}
                style={{ display: 'none' }} />
              {uploading ? '上传中…' : (attachments.length ? '继续添加凭证' : '＋ 点击上传凭证')}
            </label>
          </div>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
            <div style={{ flex: 1, paddingRight: 12 }}>
              <div style={{ fontSize: 14, color: '#475569' }}>本次请假影响课时</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, lineHeight: 1.5 }}>
                勾选后系统会自动找出你请假期间要上的课，由教务安排代课
              </div>
            </div>
            <div onClick={() => setFormHasImpact(!formHasImpact)}
              style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'all 0.3s',
                background: formHasImpact ? '#00b578' : '#e2e8f0', position: 'relative', flexShrink: 0,
              }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute',
                top: 2, left: formHasImpact ? 22 : 2, transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </div>
          </div>
          <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
            <div style={{ flex: 1, paddingRight: 12 }}>
              <div style={{ fontSize: 14, color: '#475569' }}>使用免假额度</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, lineHeight: 1.5 }}>
                免假额度只影响「不算假」，与是否需要代课无关
              </div>
            </div>
            <div onClick={() => setFormFreeLeave(!formFreeLeave)}
              style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'all 0.3s',
                background: formFreeLeave ? '#00b578' : '#e2e8f0', position: 'relative',
              }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute',
                top: 2, left: formFreeLeave ? 22 : 2, transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </div>
          </div>
          <Button type="primary" block round size="large" onClick={handleSubmit} style={{ background: '#056b4f', border: 'none' }}>提交申请</Button>
        </div>
      </Popup>

      {/* 受影响课时确认（提交后自动识别 → 教师确认） */}
      <Popup visible={showImpacts} position="bottom" round closeable onClose={() => setShowImpacts(false)} style={{ maxHeight: '85vh' }}>
        <div style={{ padding: '20px 16px 28px', overflowY: 'auto', maxHeight: 'calc(85vh - 60px)' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700 }}>确认受影响课时</h3>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
            {impactMsg || '系统已根据你的课表找出请假期间要上的课。'}
            <br />
            如果某节<strong>不需要代课</strong>（如自习课、体育课已另作安排），可标记为「免处理」。
          </p>
          {impactRows.length === 0 ? (
            <Empty description="未识别到受影响课时" />
          ) : (
            impactRows.map(row => {
              const d = row.impact_date ? new Date(row.impact_date) : null
              const wd = d ? `周${['日', '一', '二', '三', '四', '五', '六'][d.getDay()]}` : ''
              const done = row.resolution_type && row.resolution_type !== 'pending'
              return (
                <div key={row.id} style={{
                  background: '#fff', borderRadius: 12, padding: 12, marginBottom: 10,
                  border: '1px solid #e2e8f0',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                      {row.impact_date || '待定'} {wd} 第{row.period_index}节
                    </span>
                    {row.is_pe_class && <Tag color="orange" plain style={{ margin: 0 }}>体育</Tag>}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                    {row.class_name || '未知班级'} · {row.subject_name || '未知科目'}
                  </div>
                  {done ? (
                    <Tag color={row.resolution_type === 'no_impact' ? 'default' : 'green'} plain style={{ margin: 0 }}>
                      {row.resolution_type === 'no_impact' ? '无需倒课' : '已标记免处理'}
                    </Tag>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        size="small" plain round
                        onClick={() => markImpact(row, 'exempt')}
                        style={{ borderColor: '#d97706', color: '#d97706' }}
                      >
                        免处理
                      </Button>
                      <Button
                        size="small" plain round
                        onClick={() => markImpact(row, 'no_impact')}
                        style={{ borderColor: '#94a3b8', color: '#64748b' }}
                      >
                        无需倒课
                      </Button>
                    </div>
                  )}
                </div>
              )
            })
          )}
          <Button
            type="primary" block round size="large"
            onClick={() => setShowImpacts(false)}
            style={{ background: '#056b4f', border: 'none', marginTop: 8 }}
          >
            提交给教务安排
          </Button>
        </div>
      </Popup>
    </div>
  )
}
